/* eslint-disable @typescript-eslint/no-floating-promises */
import { type ApolloError } from "@apollo/client"
import { Button, useDisclosure, useToast, type ButtonProps } from "@chakra-ui/react"
import { useAtom } from "jotai"
import { useEffect, useState } from "react"
import useErrorToast from "../../../common/commonHooks/errorToast"
import { userDetailsAtom } from "../../../components/Menu/menu.store"
import { useCopyMutation } from "../common.fmp.hooks/copyfmpMutation.data.hook"
import NewFMPModal from "./fmp.createfmp.modal"

interface CopyFMPProps {
  fmpId: number | null | undefined
  projectName?: string | null | undefined
  onCopySuccess?: VoidFunction
  buttonProps?: ButtonProps
  children?: React.ReactNode
}

export const CopyFMP = ({ fmpId, onCopySuccess, projectName, buttonProps, children }: CopyFMPProps): JSX.Element => {
  const [userDetails] = useAtom(userDetailsAtom)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { copyFunction, data: copyFunctionData, loading: copyLoading, error } = useCopyMutation()
  const toast = useToast()

  useEffect(() => {
    // Error handled by useErrorToast hook
  }, [error])

  const [defaultProjectName, setDefaultProjectname] = useState<string | undefined>(undefined)

  const incrementProjectName = (projectName: string): string => {
    const match = projectName.match(/^(.*?)(\((\d+)\))?$/)
    if (match) {
      const baseName = match[1]
      const number = match[3] ? parseInt(match[3], 10) + 1 : 1
      return `${baseName}(${number})`
    }
    return projectName
  }

  const copyTriggerFunction = (): void => {
    onOpen()
    if (projectName) {
      // append (increment) to the project name
      setDefaultProjectname(`${incrementProjectName(projectName)}`)
    }
  }

  const handleError = (error: ApolloError | Error): void => {
    toast({
      title: "Error",
      description: error?.message,
      status: "error",
      duration: 5000,
      isClosable: true
    })
  }

  const showToast = (): void => {
    onClose()

    if (copyFunctionData) {
      toast({
        title: "Success",
        description: "Copied Successfully",
        status: "success",
        duration: 5000,
        isClosable: true
      })
      onCopySuccess && onCopySuccess()
    }
  }

  const modalCallBackHandler = (projectName: string): void => {
    if (fmpId === undefined || fmpId === null) {
      return
    }
    setDefaultProjectname(incrementProjectName(projectName))
    onClose()

    if (userDetails?._id) {
      const userID = userDetails?._id ?? null
      const updatedProjectName = projectName
      copyFunction({ fmpId: Number(fmpId), userId: userID ?? 0, projectName: updatedProjectName })
    } else {
      const missingUserIdError = new Error("Unable to Copy FMP.")
      handleError(missingUserIdError)
    }
  }

  useEffect(() => {
    showToast()
  }, [copyFunctionData])

  useErrorToast({ toast, error, errorMessage: "Error Copying FMP" })

  return (
    <>
      <Button isLoading={copyLoading} whiteSpace="normal" variant="linkButton" fontWeight="400" onClick={copyTriggerFunction} {...buttonProps}>
        {children ?? "Copy"}
      </Button>
      <NewFMPModal
        headerTitle="Copy FMP"
        isOpen={isOpen}
        mainButtonText="Copy"
        onClose={onClose}
        defaultProjectName={defaultProjectName}
        onCreateCallBack={modalCallBackHandler}
      />
    </>
  )
}
